<template>
  <div class="container my-4">
    <h2 class="mb-4">{{ esNuevo ? "Nueva persona" : "Editar persona" }}</h2>
    <div v-if="error" class="alert alert-danger">{{ error }}</div>
    <div v-if="cliente.activo === false" class="alert alert-warning">
      Esta persona está inactiva.
    </div>

    <form @submit.prevent="guardarCliente" class="row g-3">
      <div class="col-12">
        <fieldset class="border rounded-3 p-3">
          <legend class="float-none w-auto px-2 fs-6 mb-2">Clasificación</legend>
          <div class="d-flex flex-wrap gap-4">
            <div class="form-check">
              <input
                id="es_cliente"
                v-model="cliente.es_cliente"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label" for="es_cliente">Cliente</label>
            </div>
            <div class="form-check">
              <input
                id="es_proveedor"
                v-model="cliente.es_proveedor"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label" for="es_proveedor">Proveedor</label>
            </div>
          </div>
          <div class="form-text">Una persona puede ser cliente, proveedor o ambas.</div>
        </fieldset>
      </div>

      <div class="col-12">
        <label for="razon_social" class="form-label">Razón social</label>
        <input
          v-model="cliente.razon_social"
          id="razon_social"
          type="text"
          class="form-control"
        />
      </div>

      <!-- Nombres -->
      <div class="col-md-6">
        <label for="nombres" class="form-label">Nombres</label>
        <input
          v-model="cliente.nombres"
          id="nombres"
          type="text"
          class="form-control"
        />
      </div>

      <!-- Apellido -->
      <div class="col-md-6">
        <label for="apellido" class="form-label">Apellido</label>
        <input
          v-model="cliente.apellido"
          id="apellido"
          type="text"
          class="form-control"
        />
      </div>

      <!-- DNI -->
      <div class="col-md-4">
        <label for="dni" class="form-label">DNI</label>
        <input
          v-model="cliente.dni"
          id="dni"
          type="text"
          class="form-control"
        />
      </div>

      <!-- CUIT/CUIL -->
      <div class="col-md-8">
        <label for="cuit_cuil" class="form-label">CUIT/CUIL</label>
        <div class="input-group">
          <input
            v-model="cliente.cuit_cuil"
            id="cuit_cuil"
            type="text"
            class="form-control"
            required
          />
          <button
            type="button"
            class="btn btn-outline-secondary"
            :disabled="consultandoArca"
            @click="consultarArca"
          >
            <FontAwesomeIcon icon="magnifying-glass" />
            {{ consultandoArca ? "Consultando..." : "Consultar Arca" }}
          </button>
        </div>
      </div>

      <!-- Nro IIBB -->
      <div class="col-md-4">
        <label for="nro_iibb" class="form-label">Nro IIBB</label>
        <input
          v-model="cliente.nro_iibb"
          id="nro_iibb"
          type="text"
          class="form-control"
        />
      </div>

      <!-- Situación IVA -->
      <div class="col-md-6">
        <label for="situacion_iva" class="form-label">Situación IVA</label>
        <select
          v-model="cliente.situacion_iva"
          id="situacion_iva"
          class="form-select"
          required
        >
          <option disabled value="">Seleccionar...</option>
          <option v-for="op in opcionesIVA" :key="op" :value="op">
            {{ op }}
          </option>
        </select>
      </div>

      <div class="col-md-6">
        <label for="lista_precio_id" class="form-label">Lista de precios</label>
        <select
          v-model="cliente.lista_precio_id"
          id="lista_precio_id"
          class="form-select"
        >
          <option disabled value="">Seleccionar...</option>
          <option
            v-for="lista in listasPrecios"
            :key="lista._id"
            :value="lista._id"
          >
            {{ lista.nombre }}{{ lista.es_default ? " (default)" : "" }}
          </option>
        </select>
      </div>

      <div v-if="!esNuevo" class="col-md-6">
        <div class="form-check form-switch mt-md-4 pt-md-2">
          <input
            id="activo"
            v-model="cliente.activo"
            class="form-check-input"
            type="checkbox"
            role="switch"
          />
          <label class="form-check-label" for="activo">Activo</label>
        </div>
      </div>

      <!-- Grupos -->
      <div class="col-md-6">
        <label for="grupo_ids" class="form-label">Grupos</label>
        <div class="d-flex">
          <select
            v-model="cliente.grupo_ids"
            class="form-select me-2"
            id="grupo_ids"
            multiple
          >
            <option v-for="grupo in grupos" :key="grupo._id" :value="grupo._id">
              {{ grupo.nombre }}
            </option>
          </select>
          <button
            class="btn btn-outline-primary"
            type="button"
            @click="irAGrupos"
          >
            <FontAwesomeIcon icon="plus" />
          </button>
        </div>
      </div>

      <!-- Dirección -->
      <div class="col-md-6">
        <label for="direccion" class="form-label">Dirección</label>
        <input
          v-model="cliente.direccion"
          id="direccion"
          type="text"
          class="form-control"
        />
      </div>

      <!-- Localidad -->
      <div class="col-md-6">
        <label for="localidad" class="form-label">Localidad</label>
        <input
          v-model="cliente.localidad"
          id="localidad"
          type="text"
          class="form-control"
        />
      </div>

      <!-- Teléfono 1 -->
      <div class="col-md-4">
        <label for="telefono1" class="form-label">Teléfono 1</label>
        <input
          v-model="cliente.telefono1"
          id="telefono1"
          type="text"
          class="form-control"
        />
      </div>

      <!-- Teléfono 2 -->
      <div class="col-md-4">
        <label for="telefono2" class="form-label">Teléfono 2</label>
        <input
          v-model="cliente.telefono2"
          id="telefono2"
          type="text"
          class="form-control"
        />
      </div>

      <!-- Email -->
      <div class="col-md-4">
        <label for="email" class="form-label">Email</label>
        <input
          v-model="cliente.email"
          id="email"
          type="email"
          class="form-control"
        />
      </div>

      <!-- Botones -->
      <div class="col-12 mt-3">
        <button type="submit" class="btn btn-primary">
          <FontAwesomeIcon icon="floppy-disk" />
          {{ esNuevo ? "Guardar" : "Actualizar" }}
        </button>
        <router-link :to="rutaVolver" class="btn btn-secondary ms-2">
          <FontAwesomeIcon icon="xmark" />
          Cancelar
        </router-link>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import api from "@/api";
import { ListasPreciosApi } from "@/api/listasPrecios";

const route = useRoute();
const router = useRouter();
const id = route.params.id as string | undefined;
const esNuevo = !id;
const contextoProveedor = computed(() => route.query.tipo === "proveedor");
const rutaVolver = computed(() => contextoProveedor.value ? "/proveedores" : "/clientes");
const grupos = ref<{ _id: string; nombre: string }[]>([]);
const listasPrecios = ref<
  { _id: string; nombre: string; es_default: boolean }[]
>([]);

interface Cliente {
  _id?: string;
  razon_social?: string | null;
  nombres: string | null;
  apellido: string | null;
  dni: string | null;
  cuit_cuil: string;
  nro_iibb: string | null;
  situacion_iva: string | null;
  direccion: string | null;
  localidad: string | null;
  telefono1: string | null;
  telefono2: string | null;
  email: string | null;
  grupo_ids: string[];
  lista_precio_id?: string | null;
  es_cliente: boolean;
  es_proveedor: boolean;
  activo?: boolean;
}

const cliente = ref<Partial<Cliente>>({
  grupo_ids: [],
  es_cliente: true,
  es_proveedor: true,
});
const consultandoArca = ref(false);

const opcionesIVA = [
  "Responsable Inscripto",
  "Monotributo",
  "Exento",
  "Consumidor Final",
];
const camposRequeridos = ["cuit_cuil"];
const esRequerido = (key: string) => camposRequeridos.includes(key);
const error = ref<string | null>(null);

const limpiarCuit = (value: string | null | undefined) =>
  String(value || "").replace(/\D/g, "");

const aplicarDatosArca = (data: Record<string, any>) => {
  const cuit = limpiarCuit(data.cuit_cuil || cliente.value.cuit_cuil);
  const esJuridica =
    ["30", "33", "34"].includes(cuit.slice(0, 2)) ||
    Boolean(data?.arca?.tipo_persona?.toUpperCase?.().includes?.("JURID"));
  const razonSocialArca =
    data.razon_social ||
    (esJuridica && (data.nombre_principal || data.nombres || data.apellido)
      ? data.nombre_principal || data.nombres || data.apellido
      : null);
  const usaRazonSocial = Boolean(razonSocialArca);
  const nombres = data.nombres ?? (usaRazonSocial ? "" : cliente.value.nombres ?? "");
  const apellido = data.apellido ?? (usaRazonSocial ? "" : cliente.value.apellido ?? "");
  const razonSocialFinal =
    razonSocialArca ||
    (esJuridica ? [apellido, nombres].filter(Boolean).join(" ").trim() || null : null);
  cliente.value = {
    ...cliente.value,
    cuit_cuil: data.cuit_cuil || cliente.value.cuit_cuil || "",
    razon_social: razonSocialFinal ?? cliente.value.razon_social ?? "",
    nombres: esJuridica && (usaRazonSocial || razonSocialFinal) ? "" : nombres,
    apellido: esJuridica && (usaRazonSocial || razonSocialFinal) ? "" : apellido,
    direccion: data.direccion ?? cliente.value.direccion ?? "",
    localidad: data.localidad ?? cliente.value.localidad ?? "",
    situacion_iva: data.situacion_iva ?? cliente.value.situacion_iva ?? "",
  };
};

const consultarArca = async () => {
  const cuit = limpiarCuit(cliente.value.cuit_cuil);
  if (cuit.length !== 11) {
    error.value = "Ingresá un CUIT válido de 11 dígitos antes de consultar ARCA.";
    return;
  }

  consultandoArca.value = true;
  error.value = null;
  try {
    const res = await api.get("/clientes/consultar-arca", {
      params: { cuit },
    });
    aplicarDatosArca(res.data || {});
  } catch (err: any) {
    error.value =
      err?.response?.data?.detail?.mensaje ||
      err?.response?.data?.detail ||
      "No se pudo consultar ARCA.";
  } finally {
    consultandoArca.value = false;
  }
};

const cargarCliente = async () => {
  if (!esNuevo && id) {
    try {
      const res = await api.get(`/clientes/${id}`);
      cliente.value = res.data;
      if (cliente.value.activo === undefined) cliente.value.activo = true;
      if (cliente.value.es_cliente == null) cliente.value.es_cliente = true;
      if (cliente.value.es_proveedor == null) cliente.value.es_proveedor = true;
      if (!cliente.value.grupo_ids) cliente.value.grupo_ids = [];
      if (!cliente.value.lista_precio_id) cliente.value.lista_precio_id = "";
    } catch (err) {
      error.value =
        "No se pudo cargar el cliente. Verificá que el ID sea válido.";
    }
  }
};

const irAGrupos = () => {
  if (id) {
    router.push({ path: "/grupos", query: { volverACliente: id } });
  } else {
    router.push("/grupos");
  }
};

const listarGrupos = async () => {
  try {
    const res = await api.get("/grupos");
    grupos.value = res.data;
  } catch (err) {
    error.value = "No se pudo cargar grupos.";
  }
};

const cargarListasPrecios = async () => {
  try {
    listasPrecios.value = await ListasPreciosApi.listar();
    if (!cliente.value.lista_precio_id) {
      cliente.value.lista_precio_id =
        listasPrecios.value.find((item) => item.es_default)?._id || "";
    }
  } catch (err) {
    error.value = "No se pudieron cargar las listas de precios.";
  }
};

const guardarCliente = async () => {
  const payload = { ...cliente.value };
  delete payload._id;
  if (!payload.es_cliente && !payload.es_proveedor) {
    error.value = "La persona debe ser cliente, proveedor o ambas.";
    return;
  }
  const cuit = limpiarCuit(payload.cuit_cuil);
  if (cuit.length !== 11) {
    error.value = "El CUIT debe tener 11 dígitos.";
    return;
  }
  payload.cuit_cuil = cuit;

  for (const key in payload) {
    if (payload[key] === undefined || payload[key] === "") {
      payload[key] = null;
    }
  }

  try {
    if (id && !esNuevo) {
      await api.put(`/clientes/${id}`, payload);
    } else {
      await api.post("/clientes/", payload);
    }
    router.push(rutaVolver.value);
  } catch (e: any) {
    if (e?.response?.status === 401) {
      error.value =
        "Tu sesión expiró o no es válida. Volvé a iniciar sesión.";
    } else {
      error.value =
        e?.response?.data?.detail?.mensaje ||
        e?.response?.data?.detail ||
        "Error al guardar el cliente.";
    }
    console.error("Error guardando cliente:", e);
  }
};

onMounted(() => {
  cargarCliente();
  listarGrupos();
  cargarListasPrecios();
});
</script>
